import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { Helmet } from 'react-helmet-async';

import {
  Box,
  TextField,
  Checkbox,
  CircularProgress,
  InputAdornment,
  Button,
} from '@mui/material';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';

import verifyToken from '../../utils/verifyToken';

import { registerSchema } from '../../validation';
import { RegisterRequest } from '../../services/authentication.types';
import api from '../../services/api';
import InvalidToken from './Confirmations/InvalidToken';
import RegisteredWithSuccess from './Confirmations/RegisteredWithSucess';
import ModalComponent from '../../components/ModalComponent';

interface RegisterUser {
  token: string,
  name: string,
  surname: string,
  password: string,
  passwordConfirm: string,
  hasAgreedWithTerms: boolean
}

function Register() {
  // variables
  const { token } = useParams();
  const [isValidToken, setIsValidToken] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isRegisteredWithSuccess, setIsRegisteredWithSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [hasAgreedWithTerms, setHasAgreedWithTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [openTerms, setOpenTerms] = useState(false);
  const [isShowingPassword, setIsShowingPassword] = useState<Array<boolean>>([false, false]);

  // form
  const {
    register: addFormValues,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<RegisterRequest>({
    mode: 'onSubmit',
    resolver: yupResolver(registerSchema),
    criteriaMode: 'all',
    defaultValues: {
      token: '',
      name: '',
      surname: '',
      password: '',
      passwordConfirm: '',
      hasAgreedWithTerms: false,
    },
  });

  // effects
  // verify token when page loads or changes
  useEffect(() => {
    if (token) {
      setIsLoading(true);
      verifyToken(token, setIsValidToken, setIsLoading, setUserEmail);
    }
  }, [token]);

  // functions
  const handleCheckboxClick = () => setHasAgreedWithTerms(!hasAgreedWithTerms);

  const handleRegisterConfirm = handleSubmit(async (data: RegisterUser) => {
    setIsLoading(true);

    api.post(`/auth/register/${token}`, {
      token,
      name: data.name,
      surname: data.surname,
      password: data.password,
      passwordConfirm: data.passwordConfirm,
      hasAgreedWithTerms,
    }).then((res) => {
      const { isTokenValid } = res.data;

      if (isTokenValid) {
        setIsRegisteredWithSuccess(true);
      } else {
        setIsValidToken(false);
      }
    }).catch((error) => {
      if (error?.response?.data?.message) {
        setErrorMessage(error.response.data.message);
      }
    }).finally(() => {
      setIsLoading(false);
    });
  });

  const handleShowPassword = (event: React.MouseEvent) => {
    console.log(event.currentTarget.id);
    switch (event.currentTarget.id) {
      case '1':
        setIsShowingPassword([!isShowingPassword[0], isShowingPassword[1]]);
        break;
      case '2':
        setIsShowingPassword([isShowingPassword[0], !isShowingPassword[1]]);
        break;
      default:
        setIsShowingPassword([!isShowingPassword[0], !isShowingPassword[1]]);
        break;
    }
  };

  // render conditions
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isValidToken) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <InvalidToken />
      </Box>
    );
  }

  if (isRegisteredWithSuccess) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <RegisteredWithSuccess />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Registration - BUILD-SELF</title>
      </Helmet>
      <main className="d-flex w-100 h-100 login-background">
        <div className="container d-flex flex-column">
          <div className="vh-100">
            <div className="mx-auto d-table h-100 w-50">
              <div className="d-table-cell align-middle">
                <Box className="text-center my-4" sx={{ width: '100%' }}>
                  <Box>
                    <img
                      className="img-fluid mb-4"
                      width="180"
                      height="48"
                      src="/img/logo.png"
                      alt="BUILD-SELF"
                    />
                    <h1 className="mb-2 fw-bold">
                      Registration
                    </h1>
                    <p>
                      After entering your information, please read the terms of use
                      <br />
                      and click the &quot;Register&quot; button.
                    </p>
                  </Box>
                </Box>

                <Box className="text-danger text-center my-2 text-lg">
                  {errorMessage}
                </Box>

                <Box className="card">
                  <Box className="card-body">
                    <Box>
                      <label className="form-label w-100">
                        Last name
                      </label>
                      <TextField
                        {...addFormValues('surname')}
                        size="small"
                        placeholder="e.g. Yamada"
                        variant="outlined"
                        error={!!errors.surname}
                        helperText={errors.surname?.message || ' '}
                        fullWidth
                      />

                      <label className="form-label w-100">
                        First name
                      </label>
                      <TextField
                        {...addFormValues('name')}
                        size="small"
                        placeholder="e.g. Taro"
                        variant="outlined"
                        error={!!errors.name}
                        helperText={errors.name?.message || ' '}
                        fullWidth
                      />

                      <div>
                        <label className="form-label w-100">
                          Password
                        </label>
                        <TextField
                          {...addFormValues('password')}
                          size="small"
                          placeholder="Password"
                          variant="outlined"
                          error={!!errors.password}
                          helperText={errors.password?.message || 'Alphanumeric, 8 characters or more'}
                          type={isShowingPassword[0] ? 'text' : 'password'}
                          fullWidth
                          sx={{
                            marginBottom: '1rem',
                          }}
                          InputProps={{
                            endAdornment:
                              <InputAdornment
                                id="1"
                                onClick={handleShowPassword}
                                position="end"
                              >
                                <FontAwesomeIcon icon={isShowingPassword[0] ? faEyeSlash : faEye} />
                              </InputAdornment>,
                          }}
                        />
                      </div>
                      <div>
                        <label className="form-label w-100">
                          Password confirmation
                        </label>
                        <TextField
                          {...addFormValues('passwordConfirm')}
                          size="small"
                          placeholder="Password (confirmation)"
                          variant="outlined"
                          error={!!errors.passwordConfirm}
                          helperText={errors.passwordConfirm?.message || ' '}
                          type={isShowingPassword[1] ? 'text' : 'password'}
                          fullWidth
                          InputProps={{
                            endAdornment:
                              <InputAdornment
                                id="2"
                                onClick={handleShowPassword}
                                position="end"
                              >
                                <FontAwesomeIcon icon={isShowingPassword[1] ? faEyeSlash : faEye} />
                              </InputAdornment>,
                          }}
                        />
                      </div>

                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                          {...addFormValues('hasAgreedWithTerms')}
                          onClick={handleCheckboxClick}
                          checked={hasAgreedWithTerms}
                        />
                        <span>I agree to the&nbsp;</span>
                        <span style={{ cursor: 'pointer', color: '#3b7ddd' }} onClick={() => setOpenTerms(!openTerms)}>terms of use</span>
                      </Box>

                      <div>
                        <Button
                          variant="contained"
                          className="w-100"
                          onClick={handleRegisterConfirm}
                          disabled={!hasAgreedWithTerms}
                        >
                          Register
                        </Button>
                      </div>
                    </Box>
                  </Box>
                </Box>
              </div>
            </div>
          </div>
        </div>
      </main>

      <ModalComponent
        show={openTerms}
        title="Terms of use"
        size="lg"
        cancelButtonText="Close"
        hideConfirmButton
        bodyStyle={{ padding: 0, height: 'calc(100vh - 200px)' }}
        onConfirm={() => setOpenTerms(false)}
        closeModal={() => setOpenTerms(false)}
      >
        <iframe
          src="/kiyaku.html"
          title="Terms of use"
          width="100%"
          height="100%"
        />
      </ModalComponent>
    </>
  );
}
export default Register;
